import React, { useState, useEffect, useRef } from "react";
import "./AddressDetails.css";
import httpService from "../../services/HttpService";

const AddressDetails = ({
                            initialData = {},
                            onSubmit,
                            buttonText = "ДАЛЕЕ",
                        }) => {

    const [isSheetOpen, setSheetOpen] = useState(false);
    const [currentField, setCurrentField] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [inputValue, setInputValue] = useState(() => ({
        city: initialData?.city?.name || "",
        street: initialData?.street || "",
        house: initialData?.house || "",
        apartment: initialData?.apartment || ""
    }));

    const [cities, setCities] = useState([]);
    const [streets, setStreets] = useState([]);
    const [filteredHouses, setFilteredHouses] = useState([]);
    const [cityId, setCityId] = useState(initialData?.cityId || null);
    const [selectedAddress, setSelectedAddress] = useState("Выберите город");

    const fieldOrder = ["city", "street", "house", "apartment"];
    const bottomSheetInputRef = useRef(null);

    const displayCurrentField = {
        city: "Выберите город",
        street: "Введите улицу",
        house: "Введите дом",
        apartment: "Введите квартиру",
    };

    useEffect(() => {
        if (isSheetOpen) {
            document.body.style.overflow = "hidden";
            document.body.style.touchAction = "none";
        } else {
            document.body.style.overflow = "";
            document.body.style.touchAction = "";
        }
    }, [isSheetOpen]);

    useEffect(() => {
        const addressParts = [];
        if (inputValue.city) addressParts.push(inputValue.city);
        if (inputValue.street) addressParts.push(inputValue.street);
        if (inputValue.house) addressParts.push(inputValue.house);

        setSelectedAddress(addressParts.join(", "));
    }, [inputValue]);

    // Загрузка городов при монтировании компонента
    useEffect(() => {
        fetchCities();
    }, []);

    // Функция для получения городов
    const fetchCities = async () => {
        try {
            const response = await httpService.get("/cities");
            const data = await response.json();
            if (Array.isArray(data)) {
                setCities(data);
            } else {
                console.warn("Unexpected cities response format:", data);
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
        }
    };

    // Функция для получения улиц
    const fetchStreets = async (cityId, query) => {
        if (!cityId) return;
        try {
            const response = await httpService.get(`/address/suggestions?city_id=${cityId}&text=${encodeURIComponent(query)}`);
            const data = await response.json();
            if (Array.isArray(data)) {
                const streetTitles = data.map((item) => item.title);
                setStreets(streetTitles);
            } else {
                console.warn("Unexpected streets response format:", data);
            }
        } catch (error) {
            console.error("Error fetching streets:", error);
        }
    };

    // Функция для получения домов
    const fetchHouses = async (cityId, street, query) => {
        if (!cityId || !street || !query) return;
        try {
            const response = await httpService.get(`/address/suggestions?city_id=${cityId}&street=${encodeURIComponent(street)}&text=${encodeURIComponent(query)}`);
            const data = await response.json();
            if (Array.isArray(data)) {
                const houseTitles = data.map((item) => item.title);
                setFilteredHouses(houseTitles);
            } else {
                console.warn("Unexpected houses response format:", data);
            }
        } catch (error) {
            console.error("Error fetching houses:", error);
        }
    };

    // Обработчик клика по полю для ввода (открывает панель выбора)
    const handleInputClick = (field) => {
        setCurrentField(field);
        setSearchQuery(""); // Очищаем запрос поиска
        setSheetOpen(true);

        // Загружаем улицы или дома, если необходимо
        if (field === "street" && cityId) {
            fetchStreets(cityId, "");
        } else if (field === "house" && inputValue.street) {
            fetchHouses(cityId, inputValue.street, "");
        }
    };

    // Обработчик изменения значения поиска
    const handleSearchChange = (query) => {
        setSearchQuery(query);

        // Загружаем новые улицы или дома на основе запроса
        if (currentField === "street" && cityId) {
            fetchStreets(cityId, query);
        } else if (currentField === "house" && inputValue.street) {
            fetchHouses(cityId, inputValue.street, query);
        }
    };

    // Обработчик выбора предложения
    const handleSuggestionClick = (suggestion) => {
        setInputValue((prev) => ({ ...prev, [currentField]: suggestion }));

        // Если выбран город, обновляем ID города и очищаем улицы и дома
        if (currentField === "city") {
            const selectedCity = cities.find((city) => city.name === suggestion);
            if (selectedCity) {
                setCityId(selectedCity.id);
                inputValue.city_id = selectedCity.id
                setStreets([]);
            }
        }

        // Переходим к следующему полю, если есть
        const currentIndex = fieldOrder.indexOf(currentField);
        if (currentIndex < fieldOrder.length - 1) {
            const nextField = fieldOrder[currentIndex + 1];
            setCurrentField(nextField);
            setSearchQuery(""); // Очищаем запрос поиска
            bottomSheetInputRef.current.focus();
        } else {
            setSheetOpen(false);
        }
    };

    // Обработчик нажатия Enter в поле "Квартира"
    const handleApartmentKeyDown = (e) => {
        // Обрабатываем нажатие Enter только в случае поля "Квартира"
        if (currentField === "apartment" && e.key === "Enter") {
            // Сохраняем значение в основной инпут
            setInputValue((prev) => ({
                ...prev,
                apartment: e.target.value, // Сохраняем введенное значение в apartment
            }));
            setSheetOpen(false); // Закрываем лист после ввода
        }
    };

    // Обработчик потери фокуса поля
    const handleOnBlur = (e) => {
        // Обрабатываем нажатие Enter только в случае поля "Квартира"
        if (currentField === "apartment") {
            // Сохраняем значение в основной инпут
            setInputValue((prev) => ({
                ...prev,
                apartment: e.target.value, // Сохраняем введенное значение в apartment
            }));
            setSheetOpen(false); // Закрываем лист после ввода
        }
    };

    // Фильтрация предложений на основе запроса
    const filteredSuggestions =
        currentField === "city"
            ? cities.map((city) => city.name).filter((name) =>
                name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            : currentField === "street"
                ? streets.filter((street) =>
                    street.toLowerCase().includes(searchQuery.toLowerCase())
                )
                : currentField === "house"
                    ? filteredHouses.filter((house) =>
                        house.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    : [];

    return (
        <>
            <div className="ap-page">
                <div className="ap-form">
                    <div className="ap-title">Адрес доставки</div>
                    <form>
                        <input
                            type="text"
                            placeholder="Город"
                            className="ap-input-field"
                            value={inputValue.city}
                            onClick={() => handleInputClick("city")}
                            readOnly
                        />
                        <input
                            type="text"
                            placeholder="Улица"
                            className="ap-input-field"
                            value={inputValue.street}
                            onClick={() => handleInputClick("street")}
                            readOnly
                        />
                        <div className="ap-house-apartment">
                            <input
                                type="text"
                                placeholder="Дом"
                                className="ap-input-field ap-half-width"
                                value={inputValue.house}
                                onClick={() => handleInputClick("house")}
                                readOnly
                            />
                            <input
                                type="text"
                                placeholder="Квартира"
                                className="ap-input-field ap-half-width"
                                value={inputValue.apartment} // Значение отображается
                                onClick={() => handleInputClick("apartment")}
                                readOnly
                            />
                        </div>
                        <textarea
                            placeholder="Комментарии"
                            className="ap-input-field ap-textarea"
                        ></textarea>
                    </form>
                </div>

                <button
                    type="button"
                    className="default-button"
                    onClick={() => onSubmit(inputValue)} // Обернуто в стрелочную функцию
                >
                    {buttonText}
                </button>
            </div>

            {isSheetOpen && (
                <>
                    <div className="ap-overlay" onClick={() => setSheetOpen(false)}></div>

                    <div className="ap-bottom-sheet">
                        <div className="ap-sheet-header">
                            <p className="ap-sheet-title">{selectedAddress}</p>
                            <button
                                className="ap-close-button"
                                onClick={() => setSheetOpen(false)}
                            >
                                готово
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder={displayCurrentField[currentField]}
                            className="ap-input-field"
                            value={searchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onKeyDown={handleApartmentKeyDown}
                            onBlur={handleOnBlur}
                            ref={bottomSheetInputRef}
                        />
                        {/* Показываем список подсказок только если это не поле "Квартира" */}
                        {currentField !== "apartment" && filteredSuggestions.length > 0 && (
                            <ul className="ap-suggestion-list">
                                {filteredSuggestions?.map((suggestion, index) => (
                                    <li
                                        key={index}
                                        className="ap-suggestion-item"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default AddressDetails;
